'use client';

import { forwardRef, useId } from 'react';
import { cn } from '@/lib/utils';

interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Label content rendered next to the checkbox */
  label: React.ReactNode;
  /** Error message */
  error?: string;
}

/**
 * Styled checkbox with label. Used for consent forms.
 * Neutral border that blends with the dark footer in its default state;
 * the accent green only appears once the box is checked (or to flag an
 * error), so an unchecked box no longer reads as an active/error state.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, error, id, ...props }, ref) => {
    const generatedId = useId();
    const checkboxId = id ?? generatedId;
    const errorId = error ? `${checkboxId}-error` : undefined;

    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-start gap-4">
          <input
            ref={ref}
            type="checkbox"
            id={checkboxId}
            className={cn(
              'mt-0.5 size-6 shrink-0 cursor-pointer appearance-none border bg-transparent transition-colors',
              'border-muted/40 hover:border-muted/70',
              'checked:border-accent checked:bg-accent',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              error && 'border-red-500',
              className,
            )}
            aria-invalid={error ? true : undefined}
            aria-describedby={errorId}
            {...props}
          />
          <label htmlFor={checkboxId} className="cursor-pointer text-body leading-[1.1] text-muted">
            {label}
          </label>
        </div>
        {error && (
          <p id={errorId} className="text-small text-red-500" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);

Checkbox.displayName = 'Checkbox';
